// Mixpanel events for the AI Reports feature. Centralizes the `AI Report …`
// event names and the `args → { widgetType, country, … }` shaping so the call
// sites stay one-liners and the type/geo extraction isn't duplicated across the
// add/edit/delete/duplicate seams. Colocated with the Reports views it serves.
//
// User identity (email, domain, client tier) is attached to every event at
// login via mixpanel.identify/people.set (session.actions.js), so these
// payloads deliberately carry no user identifiers. mixpanelTrack is a no-op when
// the token is absent, so no per-call guard is needed.

import { matchWidgetType } from './widgetCatalog';
import { listViewOf } from '@/ui/components/AiAssistant/visuals';
import { mixpanelTrack } from '@/utils/app';

// A single widget's descriptive props: catalog type (id + label), data source,
// and country (the `geo` arg). `matchWidgetType` returns null for a config no
// widget can render — fall back to 'unknown' rather than dropping the field.
const widgetProps = (args = {}) => {
    const type = matchWidgetType(args);
    return {
        widgetType: type?.id ?? 'unknown',
        widgetTypeLabel: type?.label ?? null,
        dataSource: args.data_source ?? null,
        country: args.geo ?? null,
        excludedCount: args.exclude?.length ?? 0,
    };
};

export const trackReportCreated = ({ source, reportId, widgets = [] }) =>
    mixpanelTrack('AI Report Created', {
        source,
        reportId,
        widgetCount: widgets.length,
        widgetTypes: widgets.map(
            (w) => matchWidgetType(w.args)?.id ?? 'unknown',
        ),
        countries: [
            ...new Set(widgets.map((w) => w.args?.geo).filter(Boolean)),
        ],
    });

export const trackReportViewed = ({ reportId, widgetCount }) =>
    mixpanelTrack('AI Report Viewed', { reportId, widgetCount });

export const trackReportRenamed = ({ reportId, widgetCount }) =>
    mixpanelTrack('AI Report Renamed', { reportId, widgetCount });

export const trackReportReordered = ({ reportId, widgetCount }) =>
    mixpanelTrack('AI Report Widgets Reordered', { reportId, widgetCount });

export const trackReportDeleted = ({ reportId, widgetCount }) =>
    mixpanelTrack('AI Report Deleted', { reportId, widgetCount });

export const trackWidgetAdded = ({ reportId, args, widgetCount, method }) =>
    mixpanelTrack('AI Report Widget Added', {
        reportId,
        widgetCount,
        method,
        ...widgetProps(args),
    });

export const trackWidgetEdited = ({ reportId, args, widgetCount, hasTitle }) =>
    mixpanelTrack('AI Report Widget Edited', {
        reportId,
        widgetCount,
        render: listViewOf(args),
        // Presence only — free-text titles stay out of analytics.
        hasTitle: Boolean(hasTitle),
        ...widgetProps(args),
    });

// `args` are post-removal.
export const trackWidgetRowExcluded = ({ reportId, args }) =>
    mixpanelTrack('AI Report Widget Row Excluded', {
        reportId,
        ...widgetProps(args),
    });

// The on-widget combined/separated toggle; `args` are the post-toggle args.
export const trackWidgetRenderToggled = ({ reportId, args }) =>
    mixpanelTrack('AI Report Widget Render Toggled', {
        reportId,
        render: listViewOf(args),
        ...widgetProps(args),
    });

export const trackWidgetDeleted = ({ reportId, args, widgetCount }) =>
    mixpanelTrack('AI Report Widget Deleted', {
        reportId,
        widgetCount,
        ...widgetProps(args),
    });

export const trackReportDownloadStarted = ({ reportId, widgetCount }) =>
    mixpanelTrack('AI Report Download Started', { reportId, widgetCount });

export const trackReportDownloaded = ({ reportId, widgetCount, slideCount }) =>
    mixpanelTrack('AI Report Downloaded', {
        reportId,
        widgetCount,
        slideCount,
        format: 'pptx',
        mode: 'briefed',
    });

export const trackReportDownloadFailed = ({ reportId, widgetCount, reason }) =>
    mixpanelTrack('AI Report Download Failed', {
        reportId,
        widgetCount,
        reason,
    });
